import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { Segmented } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Wallet, FileText, Pencil, Trash2, Truck, ReceiptText, Package, ShieldAlert, ShieldCheck, Search, Banknote, CreditCard, ArrowLeftRight, ScrollText, CalendarDays, ArrowRight, Eye } from "lucide-react";

const methodLabel = { nakit: "Nakit", kredi_karti: "Kredi Kartı", havale: "Havale/EFT", cek: "Çek" };
const methodIcon = { nakit: Banknote, kredi_karti: CreditCard, havale: ArrowLeftRight, cek: ScrollText };
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS[Number(m) - 1] || m} ${y}`; };
const lineNet = (it) => Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_rate || 0) / 100);

const Tile = ({ label, value, sub, accent = "", testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur px-4 py-3 min-w-0" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-300/80">{label}</div>
    <div className={`font-head font-black tabular-nums text-xl mt-1 truncate ${accent}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-300/70 mt-0.5 truncate">{sub}</div>}
  </div>
);

/** Tedarikçi cari paneli — alınan ürünler (faturasız + faturalı), ödemeler, aylık özet ve borç durumu tek ekranda. */
export default function SupplierPanel({ contact, data, isAdmin, onAddEntry, onEditEntry, onDeleteEntry, onPay, onStatement, onOpenInvoice, gening }) {
  const navigate = useNavigate();
  const [view, setView] = useState("urunler");
  const [src, setSrc] = useState("all");
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("all");
  const c = contact || {};
  const vatExempt = !!c.vat_exempt;

  const entries = useMemo(() => (data?.cari_entries || []).filter((e) => e.kind === "alis"), [data]);
  const invoices = useMemo(() => (data?.invoices || []).filter((i) => i.type === "alis" && !i.is_draft), [data]);
  const payments = useMemo(() => (data?.payments || []).filter((p) => p.direction === "giden"), [data]);

  const rows = useMemo(() => {
    const out = [];
    entries.forEach((e) => (e.items || []).forEach((it, idx) => {
      if (!String(it.name || "").trim()) return;
      const stocked = (e.stock_moved || []).some((m) => m.product_id && m.product_id === it.product_id);
      out.push({ key: `e:${e.id}:${idx}`, date: e.date || "", source: "faturasiz", ref: e.doc_no ? `Belge ${e.doc_no}` : "Faturasız", entry: e, it, net: lineNet(it), stocked, by: e.created_by });
    }));
    invoices.forEach((inv) => (inv.items || []).forEach((it, idx) => {
      out.push({ key: `i:${inv.id}:${idx}`, date: inv.date || "", source: "fatura", ref: `Fatura #${inv.invoice_no || "-"}`, inv, it, net: lineNet(it), stocked: !!(inv.stock_applied && inv.stock_applied.length) });
    }));
    return out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [entries, invoices]);

  const months = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.date && s.add(String(r.date).slice(0, 7)));
    payments.forEach((p) => p.date && s.add(String(p.date).slice(0, 7)));
    return [...s].sort().reverse();
  }, [rows, payments]);

  const filtered = useMemo(() => {
    const t = q.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => (src === "all" || r.source === src)
      && (month === "all" || String(r.date).startsWith(month))
      && (!t || String(r.it.name || "").toLocaleLowerCase("tr").includes(t) || String(r.it.code || "").toLocaleLowerCase("tr").includes(t) || r.ref.toLocaleLowerCase("tr").includes(t)));
  }, [rows, src, q, month]);
  const filteredTotal = filtered.reduce((s, r) => s + r.net, 0);

  const monthly = useMemo(() => {
    const m = {};
    const add = (ym, k, v) => { if (!ym) return; m[ym] = m[ym] || { alim: 0, faturasiz: 0, fatura: 0, odeme: 0, kalem: 0 }; m[ym][k] += v; };
    entries.forEach((e) => { const ym = String(e.date || "").slice(0, 7); add(ym, "alim", Number(e.total || 0)); add(ym, "faturasiz", Number(e.total || 0)); add(ym, "kalem", (e.items || []).length); });
    invoices.forEach((i) => { const ym = String(i.date || "").slice(0, 7); add(ym, "alim", Number(i.total || 0)); add(ym, "fatura", Number(i.total || 0)); add(ym, "kalem", (i.items || []).length); });
    payments.forEach((p) => add(String(p.date || "").slice(0, 7), "odeme", Number(p.amount || 0)));
    const keys = Object.keys(m).sort();
    let run = 0;
    const list = keys.map((ym) => { run += m[ym].alim - m[ym].odeme; return { ym, ...m[ym], run }; }).reverse();
    const max = Math.max(1, ...list.map((x) => Math.max(x.alim, x.odeme)));
    return { list, max };
  }, [entries, invoices, payments]);

  const last = useMemo(() => {
    const cand = [...entries.map((e) => ({ date: e.date, total: e.total, label: "Faturasız alım" })), ...invoices.map((i) => ({ date: i.date, total: i.total, label: `Fatura #${i.invoice_no || "-"}` }))]
      .filter((x) => x.date).sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return cand[0] || null;
  }, [entries, invoices]);

  const avgDisc = useMemo(() => {
    const d = rows.filter((r) => Number(r.it.discount_rate) > 0);
    return d.length ? d.reduce((s, r) => s + Number(r.it.discount_rate), 0) / d.length : 0;
  }, [rows]);
  const stockedCount = rows.filter((r) => r.stocked).length;
  const bought = Number(c.total_bought || 0), paidOut = Number(c.paid_out || 0);
  const debt = bought - paidOut;   // >0: tedarikçiye borcumuz, <0: fazla ödeme / alacak
  const ratio = bought > 0 ? Math.min(100, Math.round((paidOut / bought) * 100)) : null;

  return (
    <div className="space-y-4" data-testid="supplier-panel">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0B192C] text-white p-4 sm:p-5" data-testid="supplier-hero">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/25 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="h-9 w-9 rounded-xl bg-primary text-white flex items-center justify-center shrink-0"><Truck className="h-5 w-5" /></span>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300/80">Tedarikçi Paneli</div>
                <div className="font-head font-black text-lg leading-tight truncate">{c.title}</div>
              </div>
              {vatExempt
                ? <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/15" data-testid="supplier-vat-badge"><ShieldCheck className="h-3 w-3 mr-1" />Açık Hesap · KDV'siz</Badge>
                : <Badge className="bg-sky-500/15 text-sky-300 border border-sky-400/30 hover:bg-sky-500/15" data-testid="supplier-vat-badge"><ShieldAlert className="h-3 w-3 mr-1" />KDV'li · Faturalı alım</Badge>}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-300/80">
              <span><b className="text-white tabular-nums">{entries.length}</b> faturasız alım</span>
              <span><b className="text-white tabular-nums">{invoices.length}</b> alış faturası</span>
              <span><b className="text-white tabular-nums">{rows.length}</b> ürün satırı</span>
              <span><b className="text-white tabular-nums">{stockedCount}</b> stoğa işlendi</span>
              {avgDisc > 0 && <span>ort. iskonto <b className="text-white tabular-nums">%{NUM(avgDisc)}</b></span>}
              {ratio != null && <span>ödeme oranı <b className="text-white tabular-nums">%{ratio}</b></span>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:w-[58%]" data-testid="supplier-kpis">
            <Tile label="Toplam Alım" value={TRY(bought)} sub={`Faturasız ${TRY(entries.reduce((s, e) => s + Number(e.total || 0), 0))}`} testid="supplier-kpi-bought" />
            <Tile label="Ödenen" value={TRY(paidOut)} sub={`${payments.length} ödeme`} accent="text-emerald-300" testid="supplier-kpi-paid" />
            <Tile label={debt >= -0.005 ? "Kalan Borç" : "Fazla Ödeme"} value={TRY(Math.abs(debt))} sub={Math.abs(debt) < 0.005 ? "Hesap kapalı" : debt > 0 ? "Tedarikçiye borcumuz" : "Tedarikçiden alacağımız"} accent={Math.abs(debt) < 0.005 ? "text-slate-200" : debt > 0 ? "text-orange-300" : "text-emerald-300"} testid="supplier-kpi-debt" />
            <Tile label="Son Alım" value={last ? fmtDate(last.date) : "—"} sub={last ? `${last.label} · ${TRY(last.total)}` : "Henüz alım yok"} testid="supplier-kpi-last" />
          </div>
        </div>
        {ratio != null && (
          <div className="relative mt-4 h-1.5 w-full rounded-full bg-white/10 overflow-hidden" title={`Ödeme oranı %${ratio}`}>
            <div className={`h-full rounded-full ${ratio >= 100 ? "bg-emerald-400" : ratio >= 50 ? "bg-sky-400" : "bg-primary"}`} style={{ width: `${ratio}%` }} />
          </div>
        )}
      </div>

      {/* Kural bandı + aksiyonlar */}
      {vatExempt ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="supplier-rule-open">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
          <p className="text-xs text-emerald-900 flex-1">Bu tedarikçi <b>Açık Hesap (KDV'siz)</b>: faturası olmadan teslim alınan mallar <b>Faturasız Alım</b> olarak kaydedilir, tutar tedarikçiye borç yazılır. "Stoğa ekle" açıkken adetler stoğa girer, maliyet alış fiyatına güncellenir.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={() => onAddEntry()} data-testid="add-alis-entry-btn"><Plus className="h-4 w-4 mr-1" />Faturasız Alım Ekle</Button>
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={onPay} data-testid="alis-pay-btn"><Wallet className="h-4 w-4 mr-1" />Ödeme Yap</Button>
            <Button size="sm" variant="outline" onClick={onStatement} disabled={gening} data-testid="alis-statement-btn"><FileText className="h-4 w-4 mr-1" />Tedarikçi Ekstresi</Button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-sky-200 bg-sky-50/70 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="supplier-rule-vat">
          <ShieldAlert className="h-5 w-5 text-sky-700 shrink-0" />
          <p className="text-xs text-sky-950 flex-1"><b>KDV'li tedarikçi:</b> faturasız alım <b>kapalı</b>. Alımlar yalnızca <b>Alış Faturası</b> ile kaydedilir (QNB'den gelen e-Faturalar otomatik düşer). Faturasız çalışılan tedarikçiyse cariyi "Açık Hesap (KDV'siz)" yapın.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" disabled title="KDV'li tedarikçide faturasız alım yapılamaz" data-testid="add-alis-entry-btn"><Plus className="h-4 w-4 mr-1" />Faturasız Alım (kapalı)</Button>
            <Button size="sm" onClick={() => navigate("/alis")} data-testid="alis-goto-invoice-btn"><ReceiptText className="h-4 w-4 mr-1" />Alış Faturası Gir<ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={onPay} data-testid="alis-pay-btn"><Wallet className="h-4 w-4 mr-1" />Ödeme Yap</Button>
            <Button size="sm" variant="outline" onClick={onStatement} disabled={gening} data-testid="alis-statement-btn"><FileText className="h-4 w-4 mr-1" />Tedarikçi Ekstresi</Button>
          </div>
        </div>
      )}

      {/* Görünüm seçici */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <Segmented value={view} onChange={setView} options={[["urunler", "Alınan Ürünler", rows.length], ["odemeler", "Ödemeler", payments.length], ["aylik", "Aylık Özet", monthly.list.length]]} testid="supplier-view" />
        {view === "urunler" && (
          <div className="flex items-center gap-2 flex-wrap lg:ml-auto">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / kod / belge ara" className="h-8 pl-8 w-52 text-xs" data-testid="supplier-search" />
            </div>
            <Segmented value={src} onChange={setSrc} options={[["all", "Tümü"], ["faturasiz", "Faturasız"], ["fatura", "Faturalı"]]} testid="supplier-src" />
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="supplier-month"><CalendarDays className="h-3.5 w-3.5 mr-1 text-muted-foreground" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm aylar</SelectItem>
                {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {view === "urunler" && (
        <div className="rounded-xl border overflow-hidden" data-testid="supplier-items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[880px]">
              <thead>
                <tr className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="p-2 text-left w-24">Tarih</th>
                  <th className="p-2 text-left w-32">Kaynak</th>
                  <th className="p-2 text-left">Ürün</th>
                  <th className="p-2 text-right w-20">Miktar</th>
                  <th className="p-2 text-right w-24">Birim Fiyat</th>
                  <th className="p-2 text-right w-16">İsk.</th>
                  <th className="p-2 text-right w-24">Net Fiyat</th>
                  <th className="p-2 text-right w-28">Tutar</th>
                  <th className="p-2 text-center w-24">Stok</th>
                  {isAdmin && <th className="p-2 w-20" />}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 10 : 9} className="p-8 text-center text-sm text-muted-foreground">{rows.length ? "Filtreye uyan ürün satırı yok." : vatExempt ? "Bu tedarikçiden henüz alım kaydı yok — “Faturasız Alım Ekle” ile başlayın." : "Bu tedarikçiden henüz alış faturası yok."}</td></tr>
                ) : filtered.map((r) => {
                  const it = r.it;
                  const netUnit = Number(it.unit_price || 0) * (1 - Number(it.discount_rate || 0) / 100);
                  return (
                    <tr key={r.key} className="border-t hover:bg-muted/30" data-testid={`supplier-row-${r.key}`}>
                      <td className="p-2 tabular-nums text-muted-foreground whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="p-2">
                        {r.source === "faturasiz"
                          ? <Badge variant="outline" className="text-[10px] py-0 border-secondary/30 bg-secondary/5 text-secondary whitespace-nowrap"><Truck className="h-3 w-3 mr-1" />{r.ref}</Badge>
                          : <button type="button" onClick={() => onOpenInvoice && onOpenInvoice(r.inv)} className="inline-flex" data-testid={`supplier-open-inv-${r.inv.id}`}><Badge variant="outline" className="text-[10px] py-0 border-sky-300 bg-sky-50 text-sky-700 whitespace-nowrap cursor-pointer hover:bg-sky-100"><ReceiptText className="h-3 w-3 mr-1" />{r.ref}</Badge></button>}
                      </td>
                      <td className="p-2 min-w-0">
                        <div className="font-medium leading-tight">{it.name}</div>
                        {(it.code || it.description) && <div className="text-[11px] text-muted-foreground font-mono truncate">{it.code || it.description}</div>}
                      </td>
                      <td className="p-2 text-right tabular-nums whitespace-nowrap">{NUM(it.quantity)} <span className="text-[10px] text-muted-foreground">{String(it.unit || "").toLocaleUpperCase("tr")}</span></td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">{TRY(it.unit_price)}</td>
                      <td className={`p-2 text-right tabular-nums whitespace-nowrap ${Number(it.discount_rate) ? "text-orange-600 font-semibold" : "text-muted-foreground"}`}>{Number(it.discount_rate) ? `%${NUM(it.discount_rate)}` : "–"}</td>
                      <td className="p-2 text-right tabular-nums whitespace-nowrap">{TRY(netUnit)}</td>
                      <td className="p-2 text-right tabular-nums font-bold whitespace-nowrap">{TRY(r.net)}</td>
                      <td className="p-2 text-center">{r.stocked ? <Badge variant="outline" className="text-[10px] py-0 border-emerald-300 bg-emerald-50 text-emerald-700"><Package className="h-3 w-3 mr-1" />Stoğa girdi</Badge> : <span className="text-[10px] text-muted-foreground">—</span>}</td>
                      {isAdmin && (
                        <td className="p-2 text-right whitespace-nowrap">
                          {r.source === "faturasiz" ? (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Kaydı düzenle" onClick={() => onEditEntry(r.entry)} data-testid={`edit-alis-${r.entry.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Kaydı sil" onClick={() => onDeleteEntry(r.entry.id)} data-testid={`del-alis-${r.entry.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                            </>
                          ) : (
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Faturayı aç" onClick={() => onOpenInvoice && onOpenInvoice(r.inv)}><Eye className="h-3.5 w-3.5 text-secondary" /></Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t bg-muted/40 text-xs">
                    <td colSpan={7} className="p-2 text-right font-semibold text-muted-foreground">{filtered.length} satır · {month === "all" ? "tüm dönem" : monthLabel(month)}{src !== "all" ? ` · ${src === "faturasiz" ? "faturasız" : "faturalı"}` : ""}</td>
                    <td className="p-2 text-right font-head font-black tabular-nums text-sm" data-testid="supplier-items-total">{TRY(filteredTotal)}</td>
                    <td colSpan={isAdmin ? 2 : 1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {view === "odemeler" && (
        <div className="space-y-2" data-testid="supplier-payments">
          {payments.length === 0 ? (
            <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">Bu tedarikçiye henüz ödeme yapılmadı.</div>
          ) : (
            <ol className="relative border-l-2 border-muted ml-3 space-y-3">
              {payments.map((p) => {
                const Icon = methodIcon[p.method] || Banknote;
                return (
                  <li key={p.id} className="ml-5 relative" data-testid={`supplier-pay-${p.id}`}>
                    <span className="absolute -left-[31px] top-2 h-4 w-4 rounded-full bg-emerald-500 ring-4 ring-emerald-100" />
                    <div className="rounded-xl border bg-card px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="text-xs tabular-nums text-muted-foreground w-20">{fmtDate(p.date)}</span>
                      <span className="inline-flex items-center gap-1 text-xs"><Icon className="h-3.5 w-3.5 text-muted-foreground" />{methodLabel[p.method] || p.method}</span>
                      {p.source === "invoice" && <Badge variant="outline" className="text-[10px] py-0 border-sky-300 bg-sky-50 text-sky-700">Fatura #{p.invoice_no || "-"}</Badge>}
                      {p.method === "cek" && p.due_date && <span className="text-[11px] text-amber-700">Vade {fmtDate(p.due_date)}{p.cleared ? " · tahsil edildi" : ""}</span>}
                      {p.note && <span className="text-[11px] text-muted-foreground italic truncate max-w-[280px]">{p.note}</span>}
                      <span className="ml-auto font-head font-black tabular-nums text-emerald-700">{TRY(p.amount)}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          <p className="text-[11px] text-muted-foreground">Ayrıntılı düzenleme / silme için <b>Ödemeler</b> sekmesini kullanın.</p>
        </div>
      )}

      {view === "aylik" && (
        <div className="rounded-xl border overflow-hidden" data-testid="supplier-monthly">
          {monthly.list.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Aylık özet için henüz hareket yok.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="p-2 text-left w-36">Ay</th>
                  <th className="p-2 text-left">Alım / Ödeme</th>
                  <th className="p-2 text-right w-28">Alım</th>
                  <th className="p-2 text-right w-28">Ödeme</th>
                  <th className="p-2 text-right w-32">Dönem Sonu Borç</th>
                </tr>
              </thead>
              <tbody>
                {monthly.list.map((m) => (
                  <tr key={m.ym} className="border-t" data-testid={`supplier-month-${m.ym}`}>
                    <td className="p-2 font-semibold whitespace-nowrap">{monthLabel(m.ym)}<div className="text-[10px] text-muted-foreground font-normal">{m.kalem} kalem{m.faturasiz > 0 && m.fatura > 0 ? ` · faturasız ${TRY(m.faturasiz)} / faturalı ${TRY(m.fatura)}` : ""}</div></td>
                    <td className="p-2">
                      <div className="space-y-1 max-w-md">
                        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-secondary rounded-full" style={{ width: `${(m.alim / monthly.max) * 100}%` }} /></div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(m.odeme / monthly.max) * 100}%` }} /></div>
                      </div>
                    </td>
                    <td className="p-2 text-right tabular-nums text-secondary font-semibold whitespace-nowrap">{TRY(m.alim)}</td>
                    <td className="p-2 text-right tabular-nums text-emerald-700 font-semibold whitespace-nowrap">{TRY(m.odeme)}</td>
                    <td className={`p-2 text-right tabular-nums font-bold whitespace-nowrap ${m.run > 0.005 ? "text-destructive" : "text-emerald-700"}`}>{TRY(m.run)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
